import { Injectable, OnDestroy, signal, computed } from '@angular/core';
import { Subscription, timer, EMPTY, catchError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ConfigurationService, ISegmentAppConfig } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';
import { SegmentSchema, SegmentService } from '@sondermind/segment';

import { SegmentPreviewEvent } from '../models/segment-preview-event.model';
import { SegmentPreviewHttpService } from './segment-preview-http.service';

const POLLING_INTERVAL_MS = 2000;
const MAX_EVENTS_BUFFER = 500;

let nextEventId = 0;

@Injectable({
  providedIn: 'root',
})
export class SegmentPreviewService implements OnDestroy {
  readonly sessionActive = signal(false);
  readonly paused = signal(false);
  readonly events = signal<SegmentPreviewEvent[]>([]);
  readonly eventCount = computed(() => this.events().length);
  readonly panelOpen = signal(false);
  readonly searchQuery = signal('');
  readonly segmentDisabled = computed(() => !!this.configurationService.env.app?.segmentDisabled);

  readonly filteredEvents = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const all = this.events();
    if (!query) {
      return all;
    }
    return all.filter((e) => e.event.toLowerCase().includes(query));
  });

  readonly filteredEventCount = computed(() => this.filteredEvents().length);

  private pollingSubscription: Subscription | null = null;
  private clientEventSubscription: Subscription | null = null;

  constructor(
    private segmentService: SegmentService<SegmentSchema>,
    private httpService: SegmentPreviewHttpService,
    private logger: Logger,
    private configurationService: ConfigurationService<ISegmentAppConfig>,
  ) {}

  ngOnDestroy(): void {
    this.teardownSubscriptions();
  }

  startSession(): void {
    if (this.sessionActive() && !this.paused()) {
      return;
    }

    if (this.paused()) {
      this.resumeSession();
      return;
    }

    this.sessionActive.set(true);
    this.paused.set(false);
    this.events.set([]);
    this.searchQuery.set('');

    this.httpService.startSession().pipe(
      catchError((err) => {
        this.logger.warn('SegmentPreview: Failed to start server session, client-only mode active', err);
        return EMPTY;
      }),
    ).subscribe();

    this.subscribeToClientEvents();
    this.startPolling();
  }

  pauseSession(): void {
    if (!this.sessionActive() || this.paused()) {
      return;
    }

    this.paused.set(true);
    this.teardownSubscriptions();

    this.httpService.stopSession().pipe(
      catchError((err) => {
        this.logger.warn('SegmentPreview: Failed to pause server session', err);
        return EMPTY;
      }),
    ).subscribe();
  }

  resetSession(): void {
    this.sessionActive.set(false);
    this.paused.set(false);
    this.events.set([]);
    this.searchQuery.set('');
    this.teardownSubscriptions();

    this.httpService.stopSession().pipe(
      catchError((err) => {
        this.logger.warn('SegmentPreview: Failed to stop server session on reset', err);
        return EMPTY;
      }),
    ).subscribe();
  }

  togglePanel(): void {
    this.panelOpen.update((open) => !open);
  }

  closePanel(): void {
    this.panelOpen.set(false);
  }

  private resumeSession(): void {
    this.paused.set(false);

    this.httpService.startSession().pipe(
      catchError((err) => {
        this.logger.warn('SegmentPreview: Failed to resume server session, client-only mode active', err);
        return EMPTY;
      }),
    ).subscribe();

    this.subscribeToClientEvents();
    this.startPolling();
  }

  private subscribeToClientEvents(): void {
    this.clientEventSubscription = this.segmentService.trackEvent$.subscribe((emission) => {
      nextEventId += 1;
      const previewEvent: SegmentPreviewEvent = {
        id: `client-${nextEventId}`,
        event: emission.event,
        properties: emission.properties,
        timestamp: emission.timestamp,
        source: 'client',
      };
      this.addEvent(previewEvent);
    });
  }

  private startPolling(): void {
    this.pollingSubscription = timer(0, POLLING_INTERVAL_MS).pipe(
      switchMap(() => {
        if (!this.sessionActive() || this.paused()) {
          return EMPTY;
        }
        return this.httpService.pollEvents().pipe(
          catchError((err) => {
            this.logger.warn('SegmentPreview: Poll failed', err);
            return EMPTY;
          }),
        );
      }),
    ).subscribe((response) => {
      if (response?.events?.length) {
        const newEvents: SegmentPreviewEvent[] = response.events.map((e) => {
          nextEventId += 1;
          return {
            ...e,
            id: `server-${nextEventId}`,
            source: 'server' as const,
          };
        });
        this.addEvents(newEvents);
      }
    });
  }

  private addEvent(event: SegmentPreviewEvent): void {
    this.events.update((existing) => {
      const updated = [event, ...existing];
      return updated.length > MAX_EVENTS_BUFFER ? updated.slice(0, MAX_EVENTS_BUFFER) : updated;
    });
  }

  private addEvents(newEvents: SegmentPreviewEvent[]): void {
    this.events.update((existing) => {
      const merged = [...newEvents.reverse(), ...existing];
      merged.sort((a, b) => b.timestamp - a.timestamp);
      return merged.length > MAX_EVENTS_BUFFER ? merged.slice(0, MAX_EVENTS_BUFFER) : merged;
    });
  }

  private teardownSubscriptions(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
    this.clientEventSubscription?.unsubscribe();
    this.clientEventSubscription = null;
  }
}
